import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { Question } from '~src/interfaces/question.interface';
import { FetchScalesParams, Scale, ScalesRespI } from '~src/interfaces/scale.interface';
import {Topic, TopicRespI} from '~src/interfaces/topic.interface';
import { fetchScales } from '~src/modules/scales/actions/scaleActions';
import { PageContentContainerS } from '~src/modules/surveys/components/details/surveyDetails.sc';
import {
  QuestionSubmit,
  addTopicQuestion,
  fetchTopicQuestions,
  fetchTopicsList,
} from '~src/modules/topics/actions/topicActions';
import { getTopic } from '~src/modules/topics/reducers/topicsReducer';

import { StoreI } from '~src/rootReducer';
import { routes } from '~src/routingConfig';
import {ApiReduxResp, ThunkMWDispatch} from '~src/services/apiMiddleware';
import logger from '~src/services/logger/LlsLogger';
import { getCompanyContentLocale } from '~src/services/session';
import TextBtnPageFooter from '~src/shared/footers/textBtnPageFooter/TextBtnPageFooter';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';

import TopicHeader from './TopicHeader';
import TopicQuestionAdd from './options/TopicQuestionAdd';
import TopicQuestionList from './options/TopicQuestionList';
import TopicScaleAdd from './scales/TopicScaleAdd';
import { AddQuestionContainer } from './topicDetails.sc';
import {isError} from "lodash";

interface StateProps {
  topic: Topic;
  scales: { [key: string]: Scale };
  loadingScales: boolean;
  defaultContentLocale: string;
  contentLocales: { [key: string]: string };
}

interface DispatchProps {
  fetchTopicsList(): ApiReduxResp<TopicRespI>;
  addTopicQuestion(topicUuid: string, value: QuestionSubmit): ApiReduxResp<Question>;
  fetchTopicQuestions(topicId: string): ApiReduxResp<Question[]>;
  fetchScales(params?: FetchScalesParams): ApiReduxResp<ScalesRespI>;
}

interface OwnProps extends RouteComponentProps<{ topicId: string }> {
  defaultContentLocale: string;
  contentLocales: { [key: string]: string };
}

type Props = OwnProps & StateProps & DispatchProps & WithTranslation;

class TopicDetail extends React.Component<Props> {
  componentDidMount() {
    if (!this.props.topic) {
      this.props
        .fetchTopicsList()
        .then((res) => {
          if (isError(res)) {
            throw new Error('Error fetching topics list');
          }
        })
        .catch((error) => {
          if (isError(error)) {
          logger.info('Error fetching topics list', {
            error,
            info: {
              componentStack: 'TopicDetail.jsx',
            },
          });
          }
        });
      this.props
        .fetchScales({ per_page: 1000 })
        .then((res) => {
          if (isError(res)) {
            throw new Error('Error fetching scales');
          }
        })
        .catch((error) => {
          if (isError(error)) {
            logger.info('Error fetching scales', {
              error,
              info: {
                componentStack: 'TopicDetail.jsx',
              },
            });
          }
        });
    }
  }

  render() {
    const { t, topic }: Props = this.props;
    return (
      <>
        {!this.props.topic ? (
          <LoadingSpinner alignCls="center-middle" />
        ) : (
          <PageContentContainerS>
            <TopicHeader topic={topic} />
            <AddQuestionContainer>
              <TopicQuestionAdd topic={topic} addTopicQuestion={this.props.addTopicQuestion} />
            </AddQuestionContainer>
            <TopicQuestionList
              topic={topic}
              defaultContentLocale={this.props.defaultContentLocale}
              contentLocales={this.props.contentLocales}
            />
            {this.props.loadingScales ? (
              <LoadingSpinner marginTop={30} />
            ) : (
              <TopicScaleAdd scales={this.props.scales} selectedTopic={topic} />
            )}
            <TextBtnPageFooter
              t={t}
              infoKey="x.topic.detail.footer.title"
              btnKey="x.topic.detail.footer.button"
              linkUrl={routes.contentSurveys.surveys}
            />
          </PageContentContainerS>
        )}
      </>
    );
  }
}

const mapStateToProps = (state: StoreI, ownProps: OwnProps): StateProps => ({
  topic: getTopic(state, ownProps.match.params.topicId),
  scales: state.scales.byId,
  loadingScales: state.scales.loadingScales,
  defaultContentLocale: getCompanyContentLocale(state),
  contentLocales: state.user.session.contentLocales,
});

const mapDispatchToProps = (dispatch: ThunkMWDispatch): DispatchProps => ({
  fetchTopicsList: () => dispatch(fetchTopicsList()),
  addTopicQuestion: (topicUuid: string, value: QuestionSubmit) =>
    dispatch(addTopicQuestion(topicUuid, value, true)),
  fetchTopicQuestions: (topicId: string) => dispatch(fetchTopicQuestions(topicId)),
  fetchScales: params => dispatch(fetchScales(params)),
});

const TopicDetailWithTranslate = withTranslation()(TopicDetail);

// @ts-expect-error ThunkMWDispatch is different from what React expects
const TopicDetailSuperComponent = connect(mapStateToProps, mapDispatchToProps)(TopicDetailWithTranslate);

export default TopicDetailSuperComponent;
